// account-ui catalog — locale nl (register: je, informal).
import en from './en';

const nl: typeof en = {
  common: {
    loading: 'Laden…',
  },
  account: {
    brand: 'lumi HR',
    home: {
      title: 'Je lumi HR-account',
      intro:
        'Deze pagina is van jou, niet van een workspace. Ze blijft bereikbaar, ook wanneer een workspace waar je bij hoort geblokkeerd of gesloten is.',
      rightsTitle: 'Wat je hier kunt doen',
      rights: {
        takeout: 'Een kopie van je gegevens downloaden zodra die voor je klaarstaat.',
        erasure:
          'Verwijdering van een workspace en zijn gegevens aanvragen, of een aanvraag intrekken die je zelf hebt ingediend.',
        invoices: 'De facturen bekijken en downloaden van een workspace die je beheert.',
        export:
          'Een workspace exporteren waarvan je eigenaar bent — alle gegevens in één versleuteld archief.',
      },
      signInHint:
        'Voor dit alles moet je eerst inloggen. Een link die we je per e-mail sturen verwijst alleen naar deze pagina — hij logt je nooit in.',
      workspaceHint:
        'Al het andere – je dagelijkse werk en je instellingen – blijft in je workspace.',
    },
    signIn: {
      title: 'Inloggen',
      intro:
        'Gebruik je lumi HR-e-mailadres en je wachtwoord. Je hoeft geen workspace te noemen — we laten je zien bij welke je hoort.',
      email: 'E-mailadres',
      password: 'Wachtwoord',
      submit: 'Inloggen',
      failed: 'We konden je met dat e-mailadres en wachtwoord niet inloggen.',
      passkey: 'Inloggen met een passkey',
      passkeyHint:
        'Geen e-mailadres? Een account dat met een mobiel nummer is ingesteld, logt in met zijn passkey.',
      passkeyFailed: 'We konden je met die passkey niet inloggen.',
      operatorSecondFactor: 'Dit is een systeembeheerdersaccount, waarvoor een tweede factor nodig is. Log hier in met een passkey; met wachtwoord en authenticatiecode log je in bij de lumi-console.',
    },
    session: {
      signedInAs: 'Ingelogd als {email}.',
      signOut: 'Uitloggen',
    },
    workspaces: {
      title: 'Jouw workspaces',
      intro:
        'De workspaces waar je bij hoort. Een geblokkeerde workspace blijft in deze lijst staan — daar is deze pagina voor.',
      emptyTitle: 'Geen workspaces',
      emptyBody:
        'Dit account hoort bij geen enkele workspace. Verwachtte je er hier een, vraag het dan aan de persoon die je heeft uitgenodigd.',
      loadFailed: 'We konden je workspaces niet laden.',
      ssoRequired:
        'Deze workspace vereist single sign-on van het bedrijf. Log in via de workspace zelf om deze functies te gebruiken — met een wachtwoord gaat dat hier niet.',
      state: {
        active: 'Actief',
        suspended: 'Geblokkeerd',
        provisioning: 'Wordt ingericht',
        erased: 'Verwijderd',
        unavailable: 'Niet beschikbaar',
      },
      hint: {
        active: 'Deze workspace werkt normaal.',
        suspended: 'Deze workspace is geblokkeerd. Je rechten kun je hier nog steeds uitoefenen.',
        provisioning: 'Deze workspace wordt nog ingericht.',
        erased:
          'Deze workspace en de gegevens ervan zijn verwijderd. Alleen de facturen blijven, omdat we die moeten bewaren.',
        unavailable: 'Deze workspace is op dit moment niet bereikbaar.',
      },
    },
    invoices: {
      open: 'Facturen tonen',
      hide: 'Facturen verbergen',
      empty: 'Voor deze workspace zijn er geen facturen.',
      loadFailed: 'We konden de facturen van deze workspace niet laden.',
      download: 'Pdf downloaden',
      documentMissing: 'Bij deze factuur hoort geen document om te downloaden.',
      documentUnavailable:
        'Het factuurdocument is op dit moment niet op te halen. Het bestaat nog steeds — probeer het zo meteen opnieuw.',
      downloadFailed: 'We konden die factuur niet downloaden.',
    },
    takeout: {
      title: 'De kopie van je gegevens',
      what:
        'Dit is een kopie van de persoonsgegevens die deze werkruimte over je bewaart, met een uitleg waarvoor ze worden gebruikt.',
      availableUntil:
        'Je kunt hem tot {date} downloaden. Daarna wordt hij automatisch verwijderd, dus sla hem eerder op als je hem wilt bewaren.',
      download: 'Mijn gegevens downloaden',
      none: 'Er staat hier op dit moment geen kopie van je gegevens voor je klaar.',
      expired:
        'De kopie die voor je is gemaakt, kon tot {date} worden gedownload. Inmiddels is hij verwijderd, zoals aangekondigd.',
      howToAsk:
        'Je kunt altijd een kopie aanvragen: neem contact op met de werkruimte die je gegevens heeft. Zodra hij klaarstaat, sturen we je een e-mail en download je hem op deze pagina.',
      loadFailed: 'We konden niet controleren of er een kopie van je gegevens voor je klaarstaat.',
      downloadUnavailable:
        'Je kopie kan nu niet worden opgehaald. Hij is er nog steeds – probeer het zo meteen opnieuw.',
      downloadFailed: 'We konden de kopie van je gegevens niet downloaden.',
    },
    export: {
      title: 'Deze workspace exporteren',
      intro:
        'Download alles uit deze workspace als één versleuteld archief. Dat werkt ook terwijl de workspace geblokkeerd is — daar is deze pagina voor.',
      contents:
        'In het archief zitten het dossier van iedere persoon, elk bestand dat in de workspace is geüpload en de koppelingen met andere diensten. Behandel het als de workspace zelf: bewaar het veilig en geef het aan niemand die niet alles mag zien. Het archief bevat ook, voor Personeel, Werving en Tijd, een HR Open Standards-document (HR-JSON) — het gedeelde formaat om je gegevens naar een andere aanbieder mee te nemen.',
      passphraseWarning:
        'Jij kiest de wachtwoordzin en wij bewaren hem nooit. Zonder die zin gaat het archief niet meer open — niet voor jou en niet voor ons.',
      passphraseLabel: 'Wachtwoordzin voor het archief',
      passphraseHint: 'Minstens {min} tekens.',
      confirmLabel: 'Herhaal de wachtwoordzin',
      submit: 'Export starten',
      started:
        'De export is gestart. Het klaarmaken duurt even — kom terug op deze pagina en ververs de lijst.',
      errorTooShort: 'Kies een wachtwoordzin van minstens {min} tekens.',
      errorMismatch: 'De twee wachtwoordzinnen zijn niet gelijk. Controleer ze en probeer het opnieuw.',
      errorCannotStart:
        'De export kan nu niet starten: mogelijk loopt er al een export van deze workspace, of de workspace wordt op dit moment gesloten.',
      errorTooMany:
        'Deze workspace heeft vandaag alle exports gestart die zijn toegestaan. Probeer het morgen opnieuw.',
      errorGeneric:
        'We konden de export niet starten. Er is niets gewijzigd – probeer het zo meteen opnieuw.',
      listTitle: 'Exports van deze workspace',
      refresh: 'Vernieuwen',
      loadFailed: 'We konden de exports van deze workspace niet laden.',
      empty: 'Van deze workspace is nog geen export gemaakt.',
      startedAt: 'Gestart op {date}',
      availableUntil: 'Te downloaden tot {date}',
      download: 'Archief downloaden',
      downloadGone: 'Dit archief is er niet meer. Start een nieuwe export om er weer een te krijgen.',
      downloadUnavailable:
        'Het archief kan nu niet worden opgehaald. Het is er nog steeds – probeer het zo meteen opnieuw.',
      downloadFailed: 'We konden het archief niet downloaden.',
      status: {
        requested: 'In de wachtrij',
        exporting: 'Wordt klaargemaakt',
        exported: 'Klaar om te downloaden',
        failed: 'Mislukt',
        expired: 'Verwijderd',
        unknown: 'Status onbekend',
      },
    },
    erasure: {
      file: 'Verwijdering van deze workspace aanvragen',
      loadFailed: 'We konden niet nagaan of er verwijdering is aangevraagd voor deze workspace.',
      confirmTitle: 'Verwijdering van deze workspace aanvragen?',
      confirmWhatGoes:
        'Alles in ‘{workspace}’ wordt dan definitief verwijderd: elk personeelsdossier, elke sollicitatie, elk document, elke urenregistratie en elke instelling, voor iedereen die erin werkt. Daarna is er niets meer terug te halen.',
      confirmProcess:
        'Je dient een aanvraag in, er wordt nog niets verwijderd. Wij beoordelen hem en na goedkeuring begint een wachttijd. Zolang de verwijdering niet echt is begonnen, kun je de aanvraag altijd intrekken.',
      invoicesKept: 'De facturen blijven staan, omdat we ze wettelijk moeten bewaren.',
      reason: 'Reden (optioneel)',
      keep: 'Nee, deze workspace houden',
      confirmSubmit: 'Ja, verwijdering aanvragen',
      pendingReview: 'Voor deze workspace is verwijdering aangevraagd. We beoordelen de aanvraag.',
      pendingUntil: 'Voor deze workspace is verwijdering aangevraagd. Hij wordt na {date} verwijderd.',
      paymentsPending:
        'Deze workspace staat gepland voor verwijdering omdat het account niet is betaald.',
      paymentsPendingUntil:
        'Deze workspace staat gepland voor verwijdering omdat het account niet is betaald. Hij wordt na {date} verwijderd.',
      paymentsSettle:
        'Als de openstaande factuur wordt betaald, stopt deze verwijdering. Je kunt de verwijdering ook zelf aanvragen — dan gaat ze door, ongeacht de betaling.',
      paymentsCannotWithdraw:
        'Deze verwijdering is gepland omdat het account niet is betaald en kan hier dus niet worden ingetrokken. Betaal de openstaande factuur om ze te stoppen.',
      confirmTakeover:
        'Deze workspace staat al gepland voor verwijdering omdat het account niet is betaald. Jouw aanvraag neemt die verwijdering over: betalen stopt ze daarna niet meer.',
      withdraw: 'Verwijderingsaanvraag intrekken',
      withdrawTitle: 'Verwijderingsaanvraag intrekken?',
      withdrawBody:
        'Deze workspace en alle gegevens blijven behouden. Je kunt verwijdering op elk moment opnieuw aanvragen.',
      confirmType: 'Typ de naam van de workspace ({name}) om te bevestigen',
      confirmMismatch:
        'De naam die je hebt getypt komt niet overeen met deze workspace. Controleer hem en probeer het opnieuw.',
      tooLate: 'De verwijdering is begonnen en kan niet meer worden ingetrokken.',
      conflict: 'Deze workspace wordt al verwijderd, er is niets veranderd.',
      nothingOpen: 'Er is voor deze workspace geen verwijderingsaanvraag om in te trekken.',
      filed: 'Je verzoek om verwijdering is ontvangen. We bekijken het.',
      withdrawn: 'Het verzoek om verwijdering is ingetrokken. Deze workspace blijft bestaan.',
    },
    closure: {
      title: 'Je lumi-account sluiten',
      intro:
        'Dit gaat over je lumi-account zelf — je aanmelding — en niet over een van de workspaces hierboven.',
      open: 'Mijn account sluiten',
      confirmTitle: 'Je lumi-account sluiten?',
      whatGoes:
        'Je aanmelding verdwijnt: dit e-mailadres en dit wachtwoord werken niet meer, een gekoppelde bedrijfsaanmelding wordt losgekoppeld en je verlaat elke workspace waar je bij hoort. Wat je zelf bij lumi hebt gedaan, gaat mee: we verwijderen je gesprekken met de assistent, de meldingen die we je hebben gestuurd en je instellingen daarvoor, en we halen je naam en je tekst uit de supportvragen en berichten die je ons hebt gestuurd.',
      whatStays:
        'Je gegevens bij elke werkgever blijven bij die werkgever. lumi levert de systemen, maar elk bedrijf blijft zelf verantwoordelijk voor zijn personeels- en sollicitatiegegevens, en je account hier sluiten verwijdert die gegevens niet. Loopt er over een workspace een rechtszaak of een officieel onderzoek, dan moeten we bewaren wat daarbij hoort: je supportvragen, berichten en gesprekken uit die workspace blijven dan staan totdat dat is afgerond.',
      howToErase:
        'Wil je dat de gegevens die een werkgever over je heeft worden gewist, vraag dat dan rechtstreeks aan dat bedrijf. Het bedrijf beslist daar zelf over, en een deel ervan moet het wettelijk nog een tijd bewaren.',
      noUndo:
        'Sluiten kan niet ongedaan worden gemaakt en we houden niets voor je open. Je kunt je altijd opnieuw aanmelden met hetzelfde e-mailadres — dat wordt een nieuw, leeg account.',
      confirmType: 'Typ je e-mailadres ({email}) om te bevestigen',
      confirmMismatch:
        'Dat is niet het e-mailadres van dit account. Controleer het en probeer het opnieuw.',
      keep: 'Nee, account behouden',
      confirmSubmit: 'Ja, mijn account sluiten',
      doneTitle: 'Je account is gesloten',
      doneBody:
        'Je bent afgemeld, en met dit e-mailadres en dit wachtwoord kun je nergens meer bij lumi inloggen. Je gesprekken met de assistent en de meldingen die we je hebben gestuurd zijn verwijderd, en in de supportvragen en berichten die je ons hebt gestuurd staan je naam en je tekst niet meer.',
      doneStays:
        'Je gegevens bij elke werkgever blijven bij die werkgever en worden daar ook beheerd. Wil je dat ze worden gewist, vraag dat dan rechtstreeks aan elk bedrijf. En loopt er over een workspace een rechtszaak of een officieel onderzoek, dan blijft bij ons staan wat daarbij hoort, totdat dat is afgerond.',
      doneMailed: 'We hebben je een bevestiging per e-mail gestuurd.',
      doneNotMailed:
        'We konden je de bevestigingsmail niet sturen. Je account is toch gesloten.',
      blockedTitle: 'Deze workspaces hebben nog een eigenaar nodig',
      blockedBody:
        'Je bent de enige eigenaar van de workspaces hieronder, dus we kunnen je account nog niet sluiten. Een workspace zonder eigenaar kunnen alleen wij weer openen, en iedereen die erin werkt zou niet meer bij de eigen gegevens kunnen.',
      blockedTransfer:
        'Draag ‘{name}’ over: open die workspace en draag het eigenaarschap daar over aan een ander lid.',
      blockedErasure:
        'Of vraag de verwijdering van ‘{name}’ aan in de lijst met je workspaces hierboven.',
      blockedReturn:
        'Deze poging onthouden we niet. Zodra geen enkele workspace je nog als eigenaar nodig heeft, kom je terug op deze pagina en sluit je je account dan.',
      unavailable:
        'Accounts sluiten werkt op dit moment niet. Er is niets veranderd — probeer het zo meteen opnieuw.',
      alreadyClosed: 'Dit account is al gesloten.',
      failed: 'Dat werkte niet. Je account is niet gesloten — probeer het zo meteen opnieuw.',
      confirmTypePhone: 'Typ je volledige mobiele nummer ({phone}) om te bevestigen',
      confirmMismatchPhone:
        'Dat is niet het mobiele nummer van dit account. Controleer het en probeer het opnieuw.',
      whatGoesPhone:
        'Je aanmelding verdwijnt: je passkeys en dit mobiele nummer werken niet meer, een gekoppelde bedrijfsaanmelding wordt losgekoppeld en je verlaat elke workspace waar je bij hoort. Wat je zelf bij lumi hebt gedaan, gaat mee: we verwijderen je gesprekken met de assistent, de meldingen die we je hebben gestuurd en je instellingen daarvoor, en we halen je naam en je tekst uit de supportvragen en berichten die je ons hebt gestuurd.',
      noUndoPhone:
        'Sluiten kan niet ongedaan worden gemaakt en we houden niets voor je open. Een workspace kan je altijd opnieuw uitnodigen met hetzelfde nummer — dat wordt een nieuw, leeg account.',
      doneBodyPhone:
        'Je bent afgemeld, en met je passkeys en dit mobiele nummer kun je nergens meer bij lumi inloggen. Je gesprekken met de assistent en de meldingen die we je hebben gestuurd zijn verwijderd, en in de supportvragen en berichten die je ons hebt gestuurd staan je naam en je tekst niet meer.',
      doneScreenOnly: 'Dit scherm is je bevestiging — per sms sturen we er geen.',
    },
  },
};

export default nl;
